import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listServiceTypesApiService } from '@/epics/serviceManagement/api/listServiceTypesApiService';
import type {
  ListServiceTypesParams,
  ServiceTypeListItem,
} from '@/epics/serviceManagement/types/serviceTypeTypes';
import type { DateRange } from '@shared/components/FilterPopup';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;
const DEFAULT_LIMIT = 10;

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const isValidIsoDate = (value: string): boolean => {
  if (!ISO_DATE_REGEX.test(value)) {
    return false;
  }
  const d = new Date(value);
  return !Number.isNaN(d.getTime());
};

const sanitizeDateParam = (value: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  return isValidIsoDate(trimmed) ? trimmed : undefined;
};

const emptyDateRange: DateRange = { startDate: '', endDate: '' };

const hasDateFilter = (range: DateRange): boolean =>
  Boolean((range.startDate ?? '').trim() || (range.endDate ?? '').trim());

export const useServiceTypeList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  const urlPage = searchParams.get('page');
  const urlLimit = searchParams.get('limit');
  const urlSearch = searchParams.get('search');
  const urlStartDate = sanitizeDateParam(searchParams.get('startDate'));
  const urlEndDate = sanitizeDateParam(searchParams.get('endDate'));

  const [serviceTypes, setServiceTypes] = useState<ServiceTypeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [limitError, setLimitError] = useState('');
  const [accessError, setAccessError] = useState(false);

  const [page, setPage] = useState(() => {
    const parsed = urlPage ? parseInt(urlPage, 10) : 1;
    return parsed < 1 ? 1 : parsed;
  });
  const [limit, setLimit] = useState(() => {
    const parsed = urlLimit ? parseInt(urlLimit, 10) : DEFAULT_LIMIT;
    return (ALLOWED_LIMITS as readonly number[]).includes(parsed)
      ? parsed
      : DEFAULT_LIMIT;
  });
  const [search, setSearch] = useState(() => {
    const value = (urlSearch || '').trim();
    return value.length > 200 ? value.substring(0, 200) : value;
  });

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    startDate: urlStartDate ?? '',
    endDate: urlEndDate ?? '',
  }));

  const [appliedFilters, setAppliedFilters] = useState<{
    dateRange: DateRange;
  }>(() => ({
    dateRange: {
      startDate: urlStartDate ?? '',
      endDate: urlEndDate ?? '',
    },
  }));

  const debouncedSearch = useDebounce(search, 500);
  const prevSearchRef = useRef<string>(search);
  const prevDebouncedSearchRef = useRef<string>(debouncedSearch);

  // Sync state FROM url changes (e.g. back/forward nav)
  useEffect(() => {
    isSyncingFromUrl.current = true;
    const currentUrlPage = searchParams.get('page');
    const currentUrlLimit = searchParams.get('limit');
    const currentUrlSearch = searchParams.get('search');
    const currentStart = sanitizeDateParam(searchParams.get('startDate'));
    const currentEnd = sanitizeDateParam(searchParams.get('endDate'));

    if (currentUrlPage) {
      const value = parseInt(currentUrlPage, 10);
      if (value >= 1 && value !== page) {
        setPage(value);
      }
    } else if (page !== 1) {
      setPage(1);
    }

    if (currentUrlLimit) {
      const value = parseInt(currentUrlLimit, 10);
      if (
        (ALLOWED_LIMITS as readonly number[]).includes(value) &&
        value !== limit
      ) {
        setLimit(value);
      }
    }

    const urlSearchValue = (currentUrlSearch || '').trim();
    if (urlSearchValue !== search.trim()) {
      const validated =
        urlSearchValue.length > 200
          ? urlSearchValue.substring(0, 200)
          : urlSearchValue;
      setSearch(validated);
    }

    const nextRange: DateRange = {
      startDate: currentStart ?? '',
      endDate: currentEnd ?? '',
    };
    if (
      nextRange.startDate !== appliedFilters.dateRange.startDate ||
      nextRange.endDate !== appliedFilters.dateRange.endDate
    ) {
      setDateRange(nextRange);
      setAppliedFilters({ dateRange: nextRange });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, appliedFilters]);

  // Sync URL FROM state
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }

    const params = new URLSearchParams();
    const prevSearchTrimmed = prevSearchRef.current.trim();
    const currentSearchTrimmed = search.trim();
    const searchChanged = prevSearchTrimmed !== currentSearchTrimmed;

    if (searchChanged) {
      prevSearchRef.current = search;
      params.set('page', '1');
      if (page !== 1) {
        setPage(1);
      }
    } else {
      params.set('page', page.toString());
    }

    params.set('limit', limit.toString());
    if (search) {
      params.set('search', search);
    }
    const appliedStart = (appliedFilters.dateRange.startDate ?? '').trim();
    const appliedEnd = (appliedFilters.dateRange.endDate ?? '').trim();
    if (appliedStart) {
      params.set('startDate', appliedStart);
    }
    if (appliedEnd) {
      params.set('endDate', appliedEnd);
    }

    const current = new URLSearchParams(searchParams.toString());
    if (params.toString() !== current.toString()) {
      setSearchParams(params, { replace: false });
    }
  }, [page, limit, search, appliedFilters, setSearchParams, searchParams]);

  const fetchServiceTypes = useCallback(async () => {
    try {
      setLoading(true);
      const trimmedSearch = debouncedSearch.trim();
      const appliedStart = (appliedFilters.dateRange.startDate ?? '').trim();
      const appliedEnd = (appliedFilters.dateRange.endDate ?? '').trim();

      const params: ListServiceTypesParams = {
        page,
        limit,
        ...(trimmedSearch && { search: trimmedSearch }),
        ...(appliedStart && { startDate: appliedStart }),
        ...(appliedEnd && { endDate: appliedEnd }),
      };

      const response =
        await listServiceTypesApiService.listServiceTypes(params);

      if (
        response.totalCount > 0 &&
        response.totalPages > 0 &&
        page > response.totalPages
      ) {
        setPage(response.totalPages);
        return;
      }

      setServiceTypes(response.data);
      setTotalCount(response.totalCount);

      const noFilters = !trimmedSearch && !appliedStart && !appliedEnd;
      setIsNoFilterFetch(noFilters);
    } catch (error) {
      if ((error as { status?: number }).status === 403) {
        setAccessError(true);
      } else {
        setServiceTypes([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, appliedFilters]);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const prevTrimmed = prevDebouncedSearchRef.current.trim();
    const currentTrimmed = debouncedSearch.trim();
    if (prevTrimmed !== currentTrimmed && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchServiceTypes();
  }, [fetchServiceTypes]);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      const normalized = newSearch.trimStart();
      if (normalized.length > 200) {
        return;
      }
      const currentTrimmed = search.trim();
      const newTrimmed = normalized.trim();
      if (!isSyncingFromUrl.current && currentTrimmed !== newTrimmed) {
        setPage(1);
      }
      if (!newTrimmed && currentTrimmed) {
        setLoading(true);
      }
      setSearch(normalized);
    },
    [search]
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ dateRange });
    setPage(1);
  }, [dateRange]);

  const handleClearFilters = useCallback(() => {
    const hadFilters = hasDateFilter(appliedFilters.dateRange);
    setDateRange(emptyDateRange);
    if (hadFilters) {
      setAppliedFilters({ dateRange: emptyDateRange });
      setPage(1);
    }
  }, [appliedFilters]);

  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !hasDateFilter(appliedFilters.dateRange);

  const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
  const currentDebouncedTrimmed = debouncedSearch.trim();
  const justClearedSearch =
    prevDebouncedTrimmed !== '' &&
    currentDebouncedTrimmed === '' &&
    !hasDateFilter(appliedFilters.dateRange);

  return {
    serviceTypes,
    loading,
    accessError,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing: isSearchClearing || justClearedSearch,
    setSearch: handleSearchChange,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchServiceTypes: fetchServiceTypes,
  };
};
